import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Card, CardActionArea, CardContent,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Divider, useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { listProjects, createProject, listRuns, listAnalyzeSessions } from "../utils/api";
import { useImpersonation } from "../contexts/ImpersonationContext";

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function statusColor(status) {
  if (status === "succeeded") return "success";
  if (status === "failed") return "error";
  return "default";
}

export default function Projects() {
  const navigate = useNavigate();
  const theme = useTheme();
  // Creating a project is a write — blocked when viewing a user read-only.
  const { viewAsReadOnly } = useImpersonation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  const [prevRuns, setPrevRuns] = useState([]);
  const [countMap, setCountMap] = useState({}); // project_id → { runs, analyses }

  useEffect(() => {
    listProjects()
      .then(setProjects)
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));

    Promise.all([
      listRuns().catch(() => []),
      listAnalyzeSessions().catch(() => []),
    ]).then(([runs, sessions]) => {
      const map = {};
      (Array.isArray(runs) ? runs : []).forEach((r) => {
        if (r.project_id) {
          if (!map[r.project_id]) map[r.project_id] = { runs: 0, analyses: 0 };
          map[r.project_id].runs += 1;
        }
      });
      (Array.isArray(sessions) ? sessions : []).forEach((s) => {
        if (s.project_id) {
          if (!map[s.project_id]) map[s.project_id] = { runs: 0, analyses: 0 };
          map[s.project_id].analyses += 1;
        }
      });
      setCountMap(map);

      const unassigned = (Array.isArray(runs) ? runs : []).filter((r) => !r.project_id);
      setPrevRuns(unassigned);
    });
  }, []);

  const handleCreate = async () => {
    if (viewAsReadOnly) return;
    if (!name.trim()) return;
    setCreating(true);
    setError("");
    try {
      const proj = await createProject({ name: name.trim(), description: description.trim() });
      setProjects((prev) => [proj, ...prev]);
      setDialogOpen(false);
      setName(""); setDescription("");
      navigate(`/projects/${proj.project_id}`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 960, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Projects</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ textTransform: "none", borderRadius: 1.5, boxShadow: "none" }}
        >
          New project
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : projects.length === 0 && prevRuns.length === 0 ? (
        // First run. A new account lands here (this is the index route), and
        // this panel used to read "No projects yet. Create one to get started"
        // pointing at /runs/new — a five-field form that asks for a warehouse
        // project and dataset name someone who signed up ten seconds ago cannot
        // know. The working front door is /analyze: drop a CSV, say what you
        // want to predict, and the target, grain and horizon are inferred.
        // Lead with that; keep project creation as the secondary path for
        // anyone who already knows they want one.
        //
        // Scoped to the genuinely-empty case, so nothing changes for an
        // existing customer — they have projects or prior runs and never reach
        // this branch.
        <Box sx={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          py: 10, color: "text.secondary", gap: 1.5, textAlign: "center",
        }}>
          <AutoGraphIcon sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            Start with your data
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: 380 }}>
            Drop in a CSV and describe what you want to predict. No setup first —
            we work out the rest from the data.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/analyze")}
            sx={{ textTransform: "none", borderRadius: 1.5, boxShadow: "none", mt: 0.5 }}
          >
            Analyze your data
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Or create a project
          </Button>
        </Box>
      ) : (
        <>
          {projects.length > 0 && (
            <>
              {(() => {
                // Split owned vs shared (backend marks is_owned_by_me on each project)
                const owned = projects.filter((p) => p.is_owned_by_me !== false);
                const shared = projects.filter((p) => p.is_owned_by_me === false);

                const renderCard = (proj) => {
                  const counts = countMap[proj.project_id];
                  const parts = [];
                  if (counts?.analyses) parts.push(`${counts.analyses} ${counts.analyses === 1 ? "analysis" : "analyses"}`);
                  if (counts?.runs) parts.push(`${counts.runs} ${counts.runs === 1 ? "run" : "runs"}`);
                  return (
                    <Card key={proj.project_id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardActionArea onClick={() => navigate(`/projects/${proj.project_id}`)}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                            <FolderOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mt: 0.3 }} />
                            <Typography variant="caption" color="text.secondary">{timeAgo(proj.created_at)}</Typography>
                          </Box>
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom noWrap>
                            {proj.name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                            {proj.is_owned_by_me === false && (
                              <Chip label="Shared" size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
                            )}
                            {proj.active_deployment_id && (
                              <Chip label="Deployed" size="small" color="success" variant="outlined" sx={{ fontSize: 11 }} />
                            )}
                          </Box>
                          {proj.description && (
                            <Typography variant="caption" color="text.secondary" sx={{
                              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                            }}>
                              {proj.description}
                            </Typography>
                          )}
                          {parts.length > 0 && (
                            <Typography variant="caption" color="text.disabled" sx={{ mt: proj.description ? 0.5 : 0, display: "block" }}>
                              {parts.join(" · ")}
                            </Typography>
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                };

                return (
                  <>
                    {owned.length > 0 && (
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 2, mb: 4 }}>
                        {owned.map(renderCard)}
                      </Box>
                    )}
                    {shared.length > 0 && (
                      <>
                        <Divider sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">Shared with you</Typography>
                        </Divider>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 2, mb: 4 }}>
                          {shared.map(renderCard)}
                        </Box>
                      </>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {prevRuns.length > 0 && (
            <Box>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Previous runs</Typography>
              </Divider>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {prevRuns.map((run, idx) => {
                  const runId = run.id || run.run_id;
                  const isLast = idx === prevRuns.length - 1;
                  return (
                    <Box
                      key={runId}
                      onClick={() => navigate(`/runs/${runId}`)}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        px: 1.5, py: 1,
                        borderBottom: isLast ? "none" : `1px solid ${theme.palette.divider}`,
                        cursor: "pointer",
                        borderRadius: isLast ? "0 0 6px 6px" : 0,
                        "&:first-of-type": { borderRadius: "6px 6px 0 0" },
                        "&:hover": { bgcolor: theme.palette.action.hover },
                        ...(prevRuns.length === 1 && { borderRadius: "6px", borderBottom: "none" }),
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {run.params?.run_name || "Untitled run"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[run.params?.dataset, timeAgo(run.created_at)].filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                      <Chip
                        label={run.status || "queued"}
                        size="small"
                        color={statusColor(run.status)}
                        variant="outlined"
                        sx={{ fontSize: 11, height: 20, ml: 2, flexShrink: 0 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New project</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField
            label="Project name" size="small" fullWidth autoFocus
            value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            disabled={viewAsReadOnly}
          />
          <TextField
            label="Description (optional)" size="small" fullWidth multiline rows={2}
            value={description} onChange={(e) => setDescription(e.target.value)}
            disabled={viewAsReadOnly}
          />
          {viewAsReadOnly && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Read-only — you're viewing another user. Creating a project is disabled.
            </Typography>
          )}
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleCreate} disabled={!name.trim() || creating || viewAsReadOnly}
            sx={{ textTransform: "none", boxShadow: "none" }}
          >
            {creating ? <CircularProgress size={16} color="inherit" /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
